import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { useEffect } from "react";

localStorage.removeItem("isLightTheme"); // Migrate from old boolean theme

// Theme types
export type Theme = "light" | "dark" | "latte" | "overripe-banana";

// Font size types
export type FontSize = "smaller" | "default" | "larger";

// Global atoms for theme and font size
export const themeAtom = atomWithStorage<Theme>("theme", "light");
export const fontSizeAtom = atomWithStorage<FontSize>("fontSize", "default");

/**
 * A component to manage the side-effects of the theme and font size.
 * This should be rendered once at the root of the application.
 */
export const ThemeEffectManager = () => {
  // Manage DOM side-effects based on theme and font size changes.
  const [theme, setTheme] = useAtom(themeAtom);
  const [fontSize, setFontSize] = useAtom(fontSizeAtom);

  // Register the theme setter for window.setTheme() on mount
  useEffect(() => {
    registerGlobalThemeSetter(setTheme);
  }, [setTheme]);

  // Register the font size setter for window.setFontSize() on mount
  useEffect(() => {
    registerGlobalFontSizeSetter(setFontSize);
  }, [setFontSize]);

  useEffect(() => {
    // Update data-theme attribute for all themes
    const htmlElement = document.documentElement;
    if (theme === "light") {
      // Light theme is the default, no attribute needed
      htmlElement.removeAttribute("data-theme");
    } else {
      // Set data-theme for dark, sepia, and overripe-banana
      htmlElement.setAttribute("data-theme", theme);
    }
  }, [theme]);

  useEffect(() => {
    // Update data-font-size attribute for font size adjustments
    const htmlElement = document.documentElement;
    if (fontSize === "default") {
      // Default font size, no attribute needed
      htmlElement.removeAttribute("data-font-size");
    } else {
      // Set data-font-size for smaller and larger
      htmlElement.setAttribute("data-font-size", fontSize);
    }
  }, [fontSize]);

  return null; // This component does not render anything
};

/**
 * Hook to get and set the current theme.
 * UI side-effects are managed by ThemeEffectManager.
 *
 * @returns {[Theme, (theme: Theme) => void]} - Current theme and setter function
 */
export function useTheme(): [Theme, (theme: Theme) => void] {
  return useAtom(themeAtom);
}

/**
 * Hook to get and set the current font size.
 * UI side-effects are managed by ThemeEffectManager.
 *
 * @returns {[FontSize, (fontSize: FontSize) => void]} - Current font size and setter function
 */
export function useFontSize(): [FontSize, (fontSize: FontSize) => void] {
  return useAtom(fontSizeAtom);
}

/**
 * @deprecated Use useTheme() instead. This is kept for backward compatibility.
 * Hook to get theme state and a setter function.
 * UI side-effects are managed by ThemeEffectManager.
 *
 * @returns {[boolean, (value: boolean) => void]} - Whether light theme is active and setter
 */
export function useThemeToggle(): [boolean, (value: boolean) => void] {
  const [theme, setTheme] = useAtom(themeAtom);

  // Convert theme to boolean (true = light, false = dark)
  const isLightTheme = theme === "light";

  // Setter that converts boolean to theme
  const setLightTheme = (value: boolean) => {
    setTheme(value ? "light" : "dark");
  };

  return [isLightTheme, setLightTheme];
}

/**
 * Global window interface extension for theme and font size testing utilities
 */
declare global {
  interface Window {
    setTheme: (theme: Theme | string) => void;
    getTheme: () => Theme;
    setFontSize: (fontSize: FontSize | string) => void;
    getFontSize: () => FontSize;
  }
}

/**
 * Initialize theme setter reference for window.setTheme()
 * This will be set by ThemeEffectManager when it mounts
 */
let globalThemeSetter: ((theme: Theme) => void) | null = null;

/**
 * Initialize font size setter reference for window.setFontSize()
 * This will be set by ThemeEffectManager when it mounts
 */
let globalFontSizeSetter: ((fontSize: FontSize) => void) | null = null;

/**
 * Register the theme setter for use by window.setTheme()
 * Called by ThemeEffectManager on mount
 */
export function registerGlobalThemeSetter(
  setter: (theme: Theme) => void,
): void {
  globalThemeSetter = setter;
}

/**
 * Register the font size setter for use by window.setFontSize()
 * Called by ThemeEffectManager on mount
 */
export function registerGlobalFontSizeSetter(
  setter: (fontSize: FontSize) => void,
): void {
  globalFontSizeSetter = setter;
}

/**
 * Global function exposed on window for testing theme switching.
 * Usage in browser console: window.setTheme('dark'), window.setTheme('sepia'), etc.
 * Fun aliases: 'cloud' for 'light', 'licorice' for 'dark'
 *
 * This function is automatically exposed when the app loads.
 */
if (typeof window !== "undefined") {
  window.setTheme = (theme: Theme | string) => {
    // Handle fun aliases
    const aliases: Record<string, Theme> = {
      cloud: "light",
      licorice: "dark",
    };
    const actualTheme = (aliases[theme.toLowerCase()] || theme) as Theme;

    const validThemes: Theme[] = ["light", "dark", "latte", "overripe-banana"];
    if (!validThemes.includes(actualTheme)) {
      console.error(
        `Invalid theme: "${theme}". Valid themes are: ${validThemes.join(", ")} (or aliases: cloud, licorice)`,
      );
      return;
    }

    // Use the registered Jotai setter if available
    if (globalThemeSetter) {
      globalThemeSetter(actualTheme);
      const displayMessage =
        theme !== actualTheme
          ? `✓ Theme changed to: ${actualTheme} (via alias: ${theme})`
          : `✓ Theme changed to: ${actualTheme}`;
      console.log(displayMessage);
    } else {
      // Fallback: update localStorage and warn user to refresh
      localStorage.setItem("theme", JSON.stringify(actualTheme));
      console.warn(
        `⚠ Theme set to: ${actualTheme}, but you may need to refresh the page for it to take effect.`,
      );
    }
  };

  // Also expose a helper to get current theme
  window.getTheme = (): Theme => {
    const stored = localStorage.getItem("theme");
    return stored ? (JSON.parse(stored) as Theme) : "light";
  };

  /**
   * Global function exposed on window for testing font size adjustment.
   * Usage in browser console: window.setFontSize('larger'), window.setFontSize('smaller'), etc.
   * Fun aliases: 'eric' for 'smaller', 'helen' for 'larger'
   */
  window.setFontSize = (fontSize: FontSize | string) => {
    // Handle fun aliases
    const aliases: Record<string, FontSize> = {
      eric: "smaller",
      helen: "larger",
    };
    const actualFontSize = (aliases[fontSize.toLowerCase()] ||
      fontSize) as FontSize;

    const validFontSizes: FontSize[] = ["smaller", "default", "larger"];
    if (!validFontSizes.includes(actualFontSize)) {
      console.error(
        `Invalid font size: "${fontSize}". Valid font sizes are: ${validFontSizes.join(", ")} (or aliases: eric, helen)`,
      );
      return;
    }

    // Use the registered Jotai setter if available
    if (globalFontSizeSetter) {
      globalFontSizeSetter(actualFontSize);
      const displayMessage =
        fontSize !== actualFontSize
          ? `✓ Font size changed to: ${actualFontSize} (via alias: ${fontSize})`
          : `✓ Font size changed to: ${actualFontSize}`;
      console.log(displayMessage);
    } else {
      // Fallback: update localStorage and warn user to refresh
      localStorage.setItem("fontSize", JSON.stringify(actualFontSize));
      console.warn(
        `⚠ Font size set to: ${actualFontSize}, but you may need to refresh the page for it to take effect.`,
      );
    }
  };

  // Also expose a helper to get current font size
  window.getFontSize = (): FontSize => {
    const stored = localStorage.getItem("fontSize");
    return stored ? (JSON.parse(stored) as FontSize) : "default";
  };
}
